import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { TurnSlots } from "../thread/slots";

/** `plan`, in the terminal register — a checklist, one step per row.
 *
 * The native skin renders this through `std-accordion` in its stepper
 * variant, which draws connectors between steps. A monospace column already
 * has the connector for free: the glyph gutter IS the vertical line, because
 * every row starts at the same character cell. */
@component({ name: "std-term-turn-plan" })
export class TermTurnPlan extends LonaWebComponent {
  private $steps: HTMLElement = this.$("steps");

  static readonly MARK: Record<TurnSlots.PlanStepStatus, string> = {
    pending: "☐",
    running: "▸",
    done: "☑",
  };

  static readonly TONE: Record<TurnSlots.PlanStepStatus, string> = {
    pending: "var(--text-ter, #8b93a7)",
    running: "var(--fg-blue, #3b82f6)",
    done: "var(--fg-green, #2da44e)",
  };

  static makeWith(props: TurnSlots.Props["plan"]): TermTurnPlan {
    return (TermTurnPlan.make() as TermTurnPlan).bind(props);
  }

  bind(props: TurnSlots.Props["plan"]): this {
    this.$steps.replaceChildren();
    for (const step of props.steps) {
      const $row = document.createElement("div");
      $row.className = "step";
      const $mark = document.createElement("span");
      $mark.className = "mark";
      $mark.textContent = TermTurnPlan.MARK[step.status];
      $mark.style.color = TermTurnPlan.TONE[step.status];
      const $label = document.createElement("span");
      $label.className = "label";
      $label.textContent = step.step;
      if (step.status === "done") $label.classList.add("done");
      $row.append($mark, $label);
      this.$steps.append($row);
    }
    return this;
  }

  static $styles = [
    css`
      :host {
        display: block;
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: var(--turn-term-size, 12px);
        line-height: var(--turn-term-leading, 1.7);
      }
      .step {
        display: flex;
        align-items: baseline;
        gap: 1ch;
      }
      .mark {
        flex: none;
        width: 1ch;
        text-align: center;
      }
      .label {
        color: var(--text-pri, #1f2328);
        overflow-wrap: anywhere;
      }
      /* Done steps recede rather than strike through: a strikethrough in a
         monospace column is a second line of ink across the text, and it
         makes the remaining work harder to find, not easier. */
      .label.done {
        color: var(--text-ter, #8b93a7);
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`<div id="steps"></div>`;
}
